import { DialogRef } from '@angular/cdk/dialog';
import { Component, effect, inject, Signal, viewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CspUtilStore, CspUtilStoreType } from '@appState';
import { DIALOG_NO } from '@models/constants';
import {
  CuiButtonComponent,
  CuiButtonCtaComponent,
  CuiCardModule,
  CuiFormComponent,
  CuiInputComponent,
  CuiPreventDialogCloseDirective,
  CuiValidators,
} from 'cui-components';

type MpnIdCheckForm = {
  mpnId: FormControl<string>;
  partnerName: FormControl<string>;
};

@Component({
  imports: [
    CuiCardModule,
    CuiButtonCtaComponent,
    CuiButtonComponent,
    CuiFormComponent,
    CuiInputComponent,
    CuiPreventDialogCloseDirective,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
  ],
  selector: 'hui-mpn-id-check-dialog',
  template: `
    <cui-card cuiPreventDialogClose>
      <cui-card-header [showCloseBtn]="true" (close)="onCancelClick()">
        <h3>Check MPN ID</h3>
      </cui-card-header>
      <cui-card-body>
        @if (form) {
          <cui-form [formGroup]="form" (save)="onCheckIdClick()">
            <cui-input [label]="'MPN ID'" formControlName="mpnId" [autoFocus]="true"></cui-input>
            @if (submittedMpnId) {
              <div class="result-div">
                @if (isLoading()) {
                  <div class="spinner-container">
                    <mat-spinner class="absolute-centered" mode="indeterminate" [diameter]="25"></mat-spinner>
                  </div>
                }
                @if (!isLoading() && retrievedPartnerName() !== null) {
                  <cui-input [label]="'Partner name'" formControlName="partnerName" [copyBtn]="true"></cui-input>
                }
                @if (!isLoading() && !retrievedPartnerName()) {
                  <div class="partner-not-found">Partner with MPN ID {{ submittedMpnId }} not found.</div>
                }
              </div>
            }
          </cui-form>
        }
      </cui-card-body>
      <cui-card-footer>
        <cui-button (clicked)="onCancelClick()">{{ DIALOG_NO }}</cui-button>
        <cui-button-cta [disabled]="(form && (form.disabled || !form.valid || !form.dirty)) || isLoading()" (clicked)="cuiForm()?.submit()">
          Check</cui-button-cta
        >
      </cui-card-footer>
    </cui-card>
  `,
  styles: `
    .result-div {
      position: relative;
      height: 2.75rem;
      .spinner-container .absolute-centered {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
      .partner-not-found {
        color: var(--color-danger);
        padding: 0.5rem 0 0 0.75rem;
        height: 100%;
      }
    }
  `,
  providers: [CspUtilStore],
})
export class MpnIdCheckDialogComponent {
  private cspUtilStore: CspUtilStoreType = inject(CspUtilStore);
  private dialogRef: DialogRef = inject(DialogRef);
  private formBuilder: FormBuilder = inject(FormBuilder);

  cuiForm: Signal<CuiFormComponent | undefined> = viewChild(CuiFormComponent);

  isLoading = this.cspUtilStore.isPartnerNameRetrievalFromMicrosoftLoading;
  retrievedPartnerName = this.cspUtilStore.retrievedPartnerNameFromMicrosoft;

  form!: FormGroup<MpnIdCheckForm>;
  submittedMpnId: string | null = null;

  readonly DIALOG_NO = DIALOG_NO;

  constructor() {
    this.cspUtilStore.reset();

    effect(() => {
      this.isLoading();

      this.form = this.formBuilder.group({
        mpnId: this.formBuilder.control(this.submittedMpnId ?? '', {
          nonNullable: true,
          validators: [CuiValidators.required, Validators.maxLength(7), CuiValidators.integersOnly],
        }),
        partnerName: this.formBuilder.control(
          { value: '', disabled: true },
          {
            nonNullable: true,
          },
        ),
      });
    });

    effect(() => {
      const partnerName = this.retrievedPartnerName();
      if (partnerName) {
        this.form.controls.partnerName.setValue(partnerName);
      }
    });
  }

  onCheckIdClick(): void {
    if (this.form && this.form.valid) {
      const { mpnId } = this.form.getRawValue();
      this.submittedMpnId = mpnId;
      this.cspUtilStore.getPartnerNameByMpnId({ req: { mpnId } });
    }
  }

  onCancelClick(): void {
    this.dialogRef.close();
  }
}
